import React, { useEffect, useMemo, useState } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Phone, Pencil, Loader2, Download, Eraser } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const AMBER = "#F59E0B";

export const VotingPersons = ({ committeeNo = "all" }) => {
  const { user } = useAuth();
  const showContact = !!user?.is_owner;
  const [data, setData] = useState(null);
  const [committee, setCommittee] = useState(committeeNo || "all");
  useEffect(() => { setCommittee(committeeNo || "all"); }, [committeeNo]);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [holder, setHolder] = useState("");
  const [holderOther, setHolderOther] = useState("");
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const [holderReport, setHolderReport] = useState(null);
  const [holderOpen, setHolderOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const clearFields = async (fields) => {
    setSaving(true);
    try {
      await api.patch(`/voters/${edit.id}`, { clear_fields: fields });
      toast.success("Bilgi temizlendi");
      setEdit(null);
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Temizlenemedi");
    } finally {
      setSaving(false);
    }
  };

  const exportHolders = async (fmt = "xlsx") => {
    setExporting(true);
    try {
      const params = { format: fmt, ...(committee !== "all" ? { committee_no: Number(committee) } : {}) };
      const res = await api.get("/reports/document-holders", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `belge_sahipleri.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally {
      setExporting(false);
    }
  };

  const exportPersons = async (fmt) => {
    setExporting(true);
    try {
      const params = { format: fmt, only: 1, order: await exportOrder(vpT.sorted.map((r) => r.id)),
        ...(committee !== "all" ? { committee_no: Number(committee) } : {}) };
      const res = await api.get("/reports/voting-persons/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oy_kullanacak_yetkililer.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally {
      setExporting(false);
    }
  };

  const openEdit = (r) => {
    setEdit(r);
    setName(r.voting_person || "");
    setPhone(r.phone || "");
    setHolder(r.document_holder || "");
    setHolderOther("");
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Oy kullanacak yetkilinin adını girin"); return; }
    const h = holder === "__other__" ? holderOther.trim() : holder;
    setSaving(true);
    try {
      await api.patch(`/voters/${edit.id}`, {
        voting_person: name.trim(),
        ...(h && h !== (edit.document_holder || "") ? { document_holder: h } : {}),
        ...(phone.trim() !== (edit.phone || "") ? { phone: phone.trim() } : {}),
      });
      toast.success("Kayıt güncellendi");
      setEdit(null);
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setData(null);
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    api.get("/reports/voting-persons", { params })
      .then(({ data }) => setData(data))
      .catch(() => { setData({ rows: [], total: 0, committees: [] }); toast.error("Liste yüklenemedi"); });
  }, [committee, reload]);

  useEffect(() => {
    const params = committee !== "all" ? { committee_no: Number(committee) } : {};
    api.get("/reports/document-holders", { params })
      .then(({ data }) => setHolderReport(data))
      .catch(() => setHolderReport(null));
  }, [committee, reload]);

  const rows = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLocaleLowerCase("tr");    return data.rows.filter((r) => {
      if (only === "missing" && r.voting_person) return false;
      if (only === "holder_missing" && r.document_holder) return false;
      if (only === "musterek" && r.representation !== "musterek") return false;
      if (!term) return true;
      return (r.company_name || "").toLocaleLowerCase("tr").includes(term)
        || (r.voting_person || "").toLocaleLowerCase("tr").includes(term)
        || String(r.sicil_no || "").includes(term);
    });
  }, [data, q, only]);
  const vpT = useTableSort(rows);
  const holdT = useTableSort(holderReport?.rows || []);


  if (!data) return <Skeleton className="h-80 w-full" />;

  return (
    <div className="space-y-3" data-testid="voting-persons">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: "Belgesi Alınan Firma", v: data.total, c: GREEN },          { l: "Müşterek Temsil", v: data.musterek_count, c: NAVY },
          { l: "Yetkili Girilmemiş", v: data.missing_count, c: AMBER },
          { l: "Belge Sahibi Girilmemiş", v: data.holder_missing_count ?? 0, c: AMBER },
        ].map((k) => (
          <Card key={k.l} className="p-3" data-testid={`vp-kpi-${k.l}`}>
            <div className="text-xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</div>
            <div className="text-xs text-muted-foreground">{k.l}</div>
          </Card>
        ))}
      </div>

      {!!data.aski_count && (
        <div className="text-xs rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-300" data-testid="vp-aski-note">
          {data.aski_count} firma yetki belgesi alınmış olsa da <b>ASKI</b> durumda olduğu için oy kullanamaz; listeye dahil edilmiyor.
        </div>
      )}

      <Card className="p-3 space-y-2" data-testid="holder-report-card">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium">Belge Sahibi Raporu — kimde kaç belge var</p>
          <span className="text-xs text-muted-foreground">{holderReport ? `${holderReport.rows.length} sahip · ${holderReport.total} belge` : "yükleniyor..."}</span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8" onClick={() => setHolderOpen((o) => !o)} data-testid="holder-report-toggle">
              {holderOpen ? "Gizle" : "Dağılımı Gör"}
            </Button>
            <Button variant="outline" size="sm" className="h-8" disabled={exporting || !holderReport?.rows?.length} onClick={() => exportHolders("xlsx")} data-testid="holder-report-export-xlsx">
              <Download className="h-4 w-4 mr-1.5" />Excel
            </Button>
            <Button variant="outline" size="sm" className="h-8" disabled={exporting || !holderReport?.rows?.length} onClick={() => exportHolders("pdf")} data-testid="holder-report-export-pdf">
              <Download className="h-4 w-4 mr-1.5" />PDF
            </Button>
          </div>
        </div>
        {holderOpen && holderReport && (
          <div className="overflow-x-auto max-h-64 overflow-y-auto rounded-md border border-border">
            <table className="w-full text-xs" data-testid="holder-report-table">
              <thead className="sticky top-0 bg-muted/90 backdrop-blur text-muted-foreground">
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <holdT.Th sortKey="hr-sahip" get={(r) => r.holder} text className="text-left font-medium py-2 px-3">Belge Kimde / Nerede</holdT.Th>
                  <holdT.Th sortKey="hr-adet" get={(r) => r.count} className="text-right font-medium py-2 px-3">Belge</holdT.Th>
                  <holdT.Th sortKey="hr-komite" get={(r) => r.committees.length} className="text-left font-medium py-2 px-3">Komite Dağılımı</holdT.Th>
                </tr>
              </thead>
              <tbody>
                {holdT.sorted.map((h) => (
                  <tr key={h.holder} className="border-t border-border/50" data-testid={`holder-report-row-${h.holder}`}>
                    <td className="py-2 px-3 font-medium">{h.holder}</td>
                    <td className="py-2 px-3 text-right tabular-nums font-semibold" style={{ color: NAVY }}>{h.count}</td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {h.committees.slice(0, 6).map((c) => `#${c.committee_no} (${c.count})`).join(", ")}
                      {h.committees.length > 6 ? ` +${h.committees.length - 6}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={committee} onValueChange={setCommittee}>
          <SelectTrigger className="h-9 w-56" data-testid="vp-committee"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Tüm komiteler</SelectItem>
            {(data.committees || []).map((c) => (
              <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={only} onValueChange={setOnly}>
          <SelectTrigger className="h-9 w-52" data-testid="vp-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="musterek">Yalnız müşterek</SelectItem>
            <SelectItem value="missing">Yetkilisi girilmemiş</SelectItem>
            <SelectItem value="holder_missing">Belge sahibi girilmemiş</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Firma, yetkili veya sicil no ara..." value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="vp-search" />
        </div>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length} onClick={() => exportPersons("xlsx")} data-testid="vp-export-xlsx">
          <Download className="h-4 w-4 mr-1.5" />Excel
        </Button>
        <Button variant="outline" size="sm" className="h-9" disabled={exporting || !rows.length} onClick={() => exportPersons("pdf")} data-testid="vp-export-pdf">
          <Download className="h-4 w-4 mr-1.5" />PDF
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground border-b border-border bg-muted/40">
            <tr>
              <vpT.Th sortKey="vp-firma" get={(r) => r.company_name} text className="text-left font-medium py-2 px-3">Firma</vpT.Th>
              <vpT.Th sortKey="vp-sicil" get={(r) => r.sicil_no} text className="text-left font-medium py-2 px-3">Sicil No</vpT.Th>
              <vpT.Th sortKey="vp-komite" get={(r) => r.committee_no} className="text-left font-medium py-2 px-3">Komite</vpT.Th>
              <vpT.Th sortKey="vp-sube" get={(r) => r.branch} text className="text-left font-medium py-2 px-3">Şube</vpT.Th>
              <vpT.Th sortKey="vp-temsil" get={(r) => r.representation} text className="text-left font-medium py-2 px-3">Temsil</vpT.Th>
              <vpT.Th sortKey="vp-yetkili" get={(r) => r.voting_person} text className="text-left font-medium py-2 px-3">Oy Kullanacak Yetkili</vpT.Th>
              <vpT.Th sortKey="vp-sahip" get={(r) => r.document_holder} text className="text-left font-medium py-2 px-3">Belge Kimde / Nerede</vpT.Th>
              {showContact && <vpT.Th sortKey="vp-tel" get={(r) => r.phone} text className="text-left font-medium py-2 px-3">Telefon</vpT.Th>}
              <vpT.Th sortKey="vp-tarih" get={(r) => r.proxy_document_date} text className="text-left font-medium py-2 px-3">Belge Tarihi</vpT.Th>
              <th className="text-right font-medium py-2 px-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {vpT.sorted.map((r) => (
              <tr key={r.id} className="border-b border-border/40 hover:bg-muted/20" data-testid={`vp-row-${r.id}`}>
                <td className="py-2 px-3 font-medium">{r.company_name}</td>
                <td className="py-2 px-3 text-muted-foreground">{r.sicil_no || "—"}</td>
                <td className="py-2 px-3">{r.committee_no ? `${r.committee_no}. ${r.committee_name}` : "—"}</td>
                <td className="py-2 px-3">{r.branch}</td>
                <td className="py-2 px-3">
                  <Badge variant="outline" className="font-normal"
                    style={r.representation === "musterek" ? { color: NAVY, borderColor: NAVY } : { color: GREEN, borderColor: GREEN }}>
                    {r.representation === "musterek" ? `Müşterek (${r.people_count})` : "Münferit"}
                  </Badge>
                </td>
                <td className="py-2 px-3 font-medium">
                  {r.voting_person || <span className="text-amber-600">girilmedi</span>}
                </td>
                <td className="py-2 px-3" data-testid={`vp-holder-${r.id}`}>
                  {r.document_holder || <span className="text-amber-600">girilmedi</span>}
                </td>
                <td className="py-2 px-3">
                  {r.phone ? (
                    <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-400 hover:underline">
                      <Phone className="h-3 w-3" />{r.phone}
                    </a>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 px-3 text-muted-foreground">{r.proxy_document_date || "—"}</td>
                <td className="py-2 px-3 text-right">
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openEdit(r)}
                    data-testid={`vp-edit-${r.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">Kayıt bulunamadı</p>}
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md" data-testid="vp-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Oy kullanacak yetkili</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{edit.company_name}</p>
              {(edit.people || []).length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Kayıtlı yetkililer</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {edit.people.map((p) => (
                      <Button key={p} type="button" variant={name === p ? "default" : "outline"} size="sm"
                        className="h-7 text-xs" onClick={() => setName(p)} data-testid={`vp-person-${p}`}>
                        {p}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Oy kullanacak yetkili (yeni isim de yazabilirsiniz)</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad"
                  data-testid="vp-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Belge kimde / nerede saklanıyor?</Label>
                <Select value={holder} onValueChange={setHolder}>
                  <SelectTrigger className="h-9" data-testid="vp-holder-select"><SelectValue placeholder="Seçin (sorumlu / Seçim Ofisi / Şube)" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {[...((data.holders || {})[String(edit.committee_no)] || []), "Merkez", "Kendi Aldı"].map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                    {edit.document_holder && ![...((data.holders || {})[String(edit.committee_no)] || []), "Merkez", "Kendi Aldı"].includes(edit.document_holder) && (
                      <SelectItem value={edit.document_holder}>{edit.document_holder}</SelectItem>
                    )}
                    <SelectItem value="__other__">Diğer (serbest yazın)</SelectItem>
                  </SelectContent>
                </Select>
                {holder === "__other__" && (
                  <Input value={holderOther} onChange={(e) => setHolderOther(e.target.value)}
                    placeholder="Örn. Avukat Bürosu, Genel Sekreterlik..." data-testid="vp-holder-other" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">İletişim numarası</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05xx xxx xx xx"
                  data-testid="vp-phone-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            {edit && (edit.voting_person || edit.document_holder) && (
              <div className="mr-auto flex flex-wrap gap-2">
                {edit.voting_person && (
                  <Button variant="outline" size="sm" className="h-8 text-red-600" disabled={saving}
                    onClick={() => clearFields(["voting_person"])} data-testid="vp-clear-person">
                    <Eraser className="h-3.5 w-3.5 mr-1.5" />Yetkiliyi temizle
                  </Button>
                )}
                {edit.document_holder && (
                  <Button variant="outline" size="sm" className="h-8 text-red-600" disabled={saving}
                    onClick={() => clearFields(["document_holder"])} data-testid="vp-clear-holder">
                    <Eraser className="h-3.5 w-3.5 mr-1.5" />Belge sahibini temizle
                  </Button>
                )}
              </div>
            )}
            <Button variant="outline" onClick={() => setEdit(null)} data-testid="vp-cancel">Vazgeç</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="vp-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-xs text-muted-foreground">
        Firmada tek yetkili varsa münferit sayılır ve belge alındığında o kişi otomatik oy kullanacak yetkili olur.
        İki veya daha fazla yetkili varsa müşterek sayılır; belge işaretlenirken hangi yetkilinin oy kullanacağı sorulur.
      </p>
    </div>
  );
};
